import { useState, type ReactNode } from 'react';
import type { ProjectPage } from '@canvas/shared-types';

interface PagesStripProps {
  pages: ProjectPage[];
  activePageId: string;
  thumbnails?: Record<string, string>;
  onSelect: (pageId: string) => void;
  onAdd: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onRename: (pageId: string, name: string) => void;
  onMove?: (pageId: string, delta: -1 | 1) => void;
  onReorder?: (fromId: string, toId: string) => void;
  /** Rendered after the built-in actions (e.g. the Motion toggle). */
  extraActions?: ReactNode;
}

export default function PagesStrip({
  pages,
  activePageId,
  thumbnails = {},
  onSelect,
  onAdd,
  onDuplicate,
  onDelete,
  onRename,
  onMove,
  onReorder,
  extraActions,
}: PagesStripProps) {
  const [dragId, setDragId] = useState<string | null>(null);
  const activeIndex = pages.findIndex((p) => p.pageId === activePageId);

  return (
    <div className="ps-pages-strip" aria-label="Pages">
      <div className="ps-pages-list">
        {pages.map((page, i) => {
          const active = page.pageId === activePageId;
          const thumb = thumbnails[page.pageId];
          return (
            <button
              key={page.pageId}
              type="button"
              draggable={Boolean(onReorder)}
              className={`ps-page-chip ${active ? 'active' : ''} ${thumb ? 'has-thumb' : ''} ${
                dragId === page.pageId ? 'dragging' : ''
              }`}
              onClick={() => onSelect(page.pageId)}
              onDoubleClick={() => {
                const next = window.prompt('Page name', page.name);
                if (next && next.trim()) onRename(page.pageId, next.trim());
              }}
              onDragStart={(e) => {
                if (!onReorder) return;
                setDragId(page.pageId);
                e.dataTransfer.setData('text/plain', page.pageId);
                e.dataTransfer.effectAllowed = 'move';
              }}
              onDragEnd={() => setDragId(null)}
              onDragOver={(e) => {
                if (!onReorder) return;
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
              }}
              onDrop={(e) => {
                if (!onReorder) return;
                e.preventDefault();
                const fromId = e.dataTransfer.getData('text/plain') || dragId;
                if (fromId) onReorder(fromId, page.pageId);
                setDragId(null);
              }}
              title={`${page.name} · drag to reorder · double-click rename`}
            >
              {thumb ? (
                <img className="ps-page-thumb" src={thumb} alt="" draggable={false} />
              ) : (
                <span className="ps-page-index">{i + 1}</span>
              )}
              <span className="ps-page-name">{page.name}</span>
            </button>
          );
        })}
      </div>
      <div className="ps-pages-actions">
        <button
          type="button"
          title="Move page left"
          disabled={!onMove || activeIndex <= 0}
          onClick={() => activePageId && onMove?.(activePageId, -1)}
        >
          ←
        </button>
        <button
          type="button"
          title="Move page right"
          disabled={!onMove || activeIndex < 0 || activeIndex >= pages.length - 1}
          onClick={() => activePageId && onMove?.(activePageId, 1)}
        >
          →
        </button>
        <button type="button" title="Add page" onClick={onAdd}>
          +
        </button>
        <button type="button" title="Duplicate page" onClick={onDuplicate}>
          Dup
        </button>
        <button
          type="button"
          title="Delete page"
          disabled={pages.length <= 1}
          onClick={onDelete}
        >
          Del
        </button>
        {extraActions}
      </div>
    </div>
  );
}
